import { useState, useEffect } from 'react';
import { usuarioService, Usuario } from '../../services';

const PERFIL_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Administrador',
  GARCOM: 'Garçom',
  COZINHA: 'Cozinha',
};

/** T124 — senha temporária forte para entregar ao funcionário */
function gerarSenhaForte(tamanho = 10): string {
  const maiusc = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const minusc = 'abcdefghijkmnpqrstuvwxyz';
  const digitos = '23456789';
  const especiais = '@#!%*';
  const todos = maiusc + minusc + digitos + especiais;
  const sorteia = (base: string) => base[Math.floor(Math.random() * base.length)];
  const base = [sorteia(maiusc), sorteia(minusc), sorteia(digitos), sorteia(especiais)];
  while (base.length < tamanho) base.push(sorteia(todos));
  return base.sort(() => Math.random() - 0.5).join('');
}

export default function Users() {
  const [users, setUsers] = useState<Usuario[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    perfil: 'COZINHA',
  });
  const [resetUserId, setResetUserId] = useState<string | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  // T124: senha oculta por padrão, com revelar/copiar sob demanda
  const [mostrarSenhaReset, setMostrarSenhaReset] = useState(false);
  const [senhaCopiada, setSenhaCopiada] = useState(false);
  // T125: edição de nome/e-mail do usuário
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: '', email: '' });
  const [erroUsuario, setErroUsuario] = useState('');

  const abrirEdicao = (user: Usuario) => {
    setEditUserId(user.id);
    setEditForm({ name: user.name, email: user.email });
    setErroUsuario('');
    setResetUserId(null);
  };

  const salvarEdicao = async () => {
    if (!editUserId) return;
    try {
      setErroUsuario('');
      await usuarioService.update(editUserId, {
        name: editForm.name.trim(),
        email: editForm.email.trim().toLowerCase(),
      });
      setEditUserId(null);
      loadUsers();
    } catch (error) {
      const msg = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setErroUsuario(msg || 'Não foi possível salvar as alterações.');
    }
  };

  const copiarSenha = async (senha: string) => {
    try {
      await navigator.clipboard.writeText(senha);
      setSenhaCopiada(true);
      setTimeout(() => setSenhaCopiada(false), 2000);
    } catch {
      // navegador sem permissão de clipboard: o usuário vê a senha e digita
      setMostrarSenhaReset(true);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await usuarioService.findAll();
      setUsers(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      alert('Erro ao carregar usuários');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await usuarioService.create(formData);
      setShowForm(false);
      setFormData({ name: '', email: '', password: '', perfil: 'COZINHA' });
      await loadUsers();
    } catch (error: any) {
      console.error('Erro ao salvar usuário:', error);
      alert(error.response?.data?.message || 'Erro ao criar usuário');
    }
  };

  const handleToggleActive = async (user: Usuario) => {
    try {
      await usuarioService.update(user.id, { is_active: !user.is_active });
      await loadUsers();
    } catch (error: any) {
      console.error('Erro ao atualizar usuário:', error);
      alert(error.response?.data?.message || 'Erro ao atualizar usuário');
    }
  };

  const handleResetPassword = async (userId: string) => {
    if (resetPassword.length < 6) {
      alert('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }

    try {
      await usuarioService.resetPassword(userId, resetPassword);
      setResetUserId(null);
      setResetPassword('');
      alert('Senha redefinida com sucesso');
    } catch (error: any) {
      console.error('Erro ao redefinir senha:', error);
      alert(error.response?.data?.message || 'Erro ao redefinir senha');
    }
  };

  if (isLoading) {
    return <div className="text-center py-12">Carregando...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-100">👥 Usuários</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn-primary"
        >
          {showForm ? 'Fechar' : 'Novo Usuário'}
        </button>
      </div>

      <div className="mb-4 text-sm text-gray-400">
        Crie contas para o pessoal do restaurante. O papel vale só aqui (Administrador tem
        acesso total deste restaurante; Garçom e Cozinha veem apenas os pedidos).
      </div>

      {showForm && (
        <div className="card mb-6">
          <h2 className="text-xl font-bold mb-4">Novo Usuário</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Nome *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-field"
                  placeholder="Cozinha 1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-field"
                  placeholder="cozinha@cardapio.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Senha *</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="input-field"
                  placeholder="Mínimo 6 caracteres"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Perfil</label>
                <select
                  value={formData.perfil}
                  onChange={(e) => setFormData({ ...formData, perfil: e.target.value })}
                  className="input-field"
                >
                  <option value="COZINHA">Cozinha</option>
                  <option value="GARCOM">Garçom</option>
                  <option value="SUPER_ADMIN">Administrador</option>
                </select>
              </div>
            </div>
            <div className="flex space-x-2">
              <button type="submit" className="btn-primary">
                Criar
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="btn-secondary"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead>
            <tr className="border-b">
              <th className="text-left py-3 px-4">Nome</th>
              <th className="text-left py-3 px-4">Email</th>
              <th className="text-left py-3 px-4">Perfil</th>
              <th className="text-left py-3 px-4">Status</th>
              <th className="text-right py-3 px-4">Ações</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className="border-b hover:bg-gray-700/50">
                <td className="py-3 px-4 font-medium">{user.name}</td>
                <td className="py-3 px-4 text-gray-400">{user.email}</td>
                <td className="py-3 px-4">{PERFIL_LABELS[user.perfil ?? ''] || '—'}</td>
                <td className="py-3 px-4">
                  <button
                    onClick={() => handleToggleActive(user)}
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      user.is_active
                        ? 'bg-green-900/60 text-green-300'
                        : 'bg-red-900/60 text-red-300'
                    }`}
                  >
                    {user.is_active ? 'Ativo' : 'Inativo'}
                  </button>
                </td>
                <td className="py-3 px-4 text-right space-x-2">
                  <button
                    onClick={() => abrirEdicao(user)}
                    className="text-primary-400 hover:text-primary-300"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => {
                      setResetUserId(resetUserId === user.id ? null : user.id);
                      setResetPassword('');
                      setEditUserId(null);
                    }}
                    className="text-primary-400 hover:text-primary-300"
                  >
                    Redefinir Senha
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        {/* T125: editar nome/e-mail — corrigir um e-mail digitado errado não
            exige mais criar outro usuário */}
        {editUserId && (
          <div className="border-t pt-4 mt-4">
            <p className="text-sm font-semibold text-gray-200 mb-3">
              Editar usuário: {users.find((u) => u.id === editUserId)?.name}
            </p>
            {erroUsuario && <p className="text-sm text-red-400 mb-3">{erroUsuario}</p>}
            <div className="flex flex-col sm:flex-row sm:items-end gap-2">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-300 mb-1">Nome</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="input-field"
                  minLength={2}
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-300 mb-1">E-mail</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="input-field"
                />
              </div>
              <button onClick={salvarEdicao} className="btn-primary">
                Salvar
              </button>
              <button onClick={() => setEditUserId(null)} className="btn-secondary">
                Cancelar
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Trocar o e-mail muda o login deste funcionário — avise quem usa a conta.
            </p>
          </div>
        )}

        {resetUserId && (
          <div className="border-t pt-4 mt-4">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Nova senha para este usuário
                </label>
                {/* T124: senha nunca visível de bandeja no balcão */}
                <div className="relative">
                  <input
                    type={mostrarSenhaReset ? 'text' : 'password'}
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    className="input-field pr-12"
                    placeholder="Mínimo 6 caracteres"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setMostrarSenhaReset((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200 text-sm"
                    aria-label={mostrarSenhaReset ? 'Ocultar senha' : 'Mostrar senha'}
                    title={mostrarSenhaReset ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {mostrarSenhaReset ? '🙈' : '👁️'}
                  </button>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <button
                    type="button"
                    onClick={() => setResetPassword(gerarSenhaForte())}
                    className="text-xs text-primary-400 hover:text-primary-300"
                  >
                    Gerar senha forte
                  </button>
                  {resetPassword && (
                    <button
                      type="button"
                      onClick={() => copiarSenha(resetPassword)}
                      className="text-xs text-primary-400 hover:text-primary-300"
                    >
                      {senhaCopiada ? '✅ Copiada!' : 'Copiar senha'}
                    </button>
                  )}
                </div>
              </div>
              <button
                onClick={() => handleResetPassword(resetUserId)}
                className="btn-primary"
              >
                Salvar Nova Senha
              </button>
              <button
                onClick={() => setResetUserId(null)}
                className="btn-secondary"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
